import { useCallback, useEffect, useState, useRef } from "react";
import { chatBotDefaultOptions } from "./constants";
import { VoiceBotOptions } from "./types";

const mergeWithDefaults = (stored: Partial<VoiceBotOptions>, defaults: VoiceBotOptions): VoiceBotOptions => {
    const result = { ...defaults };

    for (const key in defaults) {
        const typedKey = key as keyof VoiceBotOptions;
        const storedValue = stored[typedKey];

        // Only use stored value if it's defined and not an empty string
        if (storedValue !== undefined && storedValue !== null && storedValue !== "") {
            // @ts-ignore - We know the types match because we're iterating over the defaults
            result[typedKey] = storedValue;
        }
    }

    return result;
};

const useLocalStorage = (key: string, initialValue: VoiceBotOptions) => {
    const [value, setValue] = useState<VoiceBotOptions>(() => {
        const storedValue = localStorage.getItem(key);
        try {
            const storedOptions = storedValue ? JSON.parse(storedValue) : {};
            return mergeWithDefaults(storedOptions, initialValue);
        } catch {
            return initialValue;
        }
    });

    useEffect(() => {
        localStorage.setItem(key, JSON.stringify(value));
    }, [key, value]);

    return [value, setValue] as const;
};

export const useChatBotContext = (): [VoiceBotOptions, (newOptions: Partial<VoiceBotOptions>) => void] => {
    const [options, setOptionsInternal] = useLocalStorage("chatBotOptions", chatBotDefaultOptions);
    const optionsRef = useRef(options);

    useEffect(() => {
        optionsRef.current = options;
    }, [options]);

    const setOptions = useCallback(
        (newOptions: Partial<VoiceBotOptions>) => {
            setOptionsInternal((prev) => ({
                ...prev,
                ...newOptions,
            }));
        },
        [setOptionsInternal]
    );

    return [options, setOptions];
};
